import { WorkspaceActionTypes } from './constants';

export interface WorkspaceData {
    id: number;
    workspace_name: string;
    workspace_slug: string;
    workspace_type: 'personal' | 'business';
    owner_user_id: number;
    status: string;
    role: string;
    role_id?: number | null;
    role_name?: string | null;
    /** Set only for a member with a custom role: item key => allowed actions. */
    permissions?: Record<string, string[]> | null;
    member_count: number;
    joined_at?: string;
    created_at?: string;
}

export interface MemberData {
    id: number;
    user_id: number;
    name: string;
    email: string;
    profile?: string;
    role: string;
    status: string;
    joined_at?: string;
}

export interface InvitationData {
    id: number;
    email: string;
    role: string;
    expires_at?: string;
    status: string;
    invited_by?: string;
    created_at?: string;
}

interface WorkspaceState {
    workspaces: WorkspaceData[];
    activeWorkspace: WorkspaceData | null;
    members: MemberData[];
    pendingInvitations: InvitationData[];
    loading: boolean;
    error?: string | null;
    memberError?: string | null;
    inviteSuccess?: boolean;
    createSuccess?: boolean;
    switchSuccess?: boolean;
    removeSuccess?: boolean;
    updateRoleSuccess?: boolean;
    cancelSuccess?: boolean;
    acceptSuccess?: boolean;
    acceptedWorkspace?: WorkspaceData | null;
}

// ── Persist active workspace in localStorage ────────────────────────
const WORKSPACE_KEY = 'active_workspace';

const getStoredWorkspace = (): WorkspaceData | null => {
    try {
        const stored = sessionStorage.getItem(WORKSPACE_KEY) || localStorage.getItem(WORKSPACE_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
};

const INIT_STATE: WorkspaceState = {
    workspaces: [],
    activeWorkspace: getStoredWorkspace(),
    members: [],
    pendingInvitations: [],
    loading: false,
    error: null,
};

interface WorkspaceAction {
    type: WorkspaceActionTypes;
    payload: {
        actionType?: string;
        data?: any;
        error?: string;
    };
}

const Workspace = (state: WorkspaceState = INIT_STATE, action: WorkspaceAction): WorkspaceState => {
    switch (action.type) {

        case WorkspaceActionTypes.API_RESPONSE_SUCCESS:
            switch (action.payload.actionType) {

                case WorkspaceActionTypes.FETCH_WORKSPACES: {
                    const list = action.payload.data?.workspaces ?? [];
                    // Refresh the active workspace from the list so a changed role or permissions apply without a re-login.
                    const fresh = state.activeWorkspace ? list.find((w: any) => w.id === state.activeWorkspace?.id) : null;
                    if (fresh) {
                        sessionStorage.setItem(WORKSPACE_KEY, JSON.stringify(fresh));
                        localStorage.setItem(WORKSPACE_KEY, JSON.stringify(fresh));
                    }
                    return {
                        ...state,
                        loading: false,
                        workspaces: list,
                        activeWorkspace: fresh ?? state.activeWorkspace,
                    };
                }

                case WorkspaceActionTypes.SWITCH_WORKSPACE: {
                    const ws = action.payload.data?.workspace ?? null;
                    if (ws) {
                        sessionStorage.setItem(WORKSPACE_KEY, JSON.stringify(ws));
                        localStorage.setItem(WORKSPACE_KEY, JSON.stringify(ws));
                    }
                    return {
                        ...state,
                        loading: false,
                        switchSuccess: true,
                        activeWorkspace: ws,
                        error: null,
                    };
                }

                case WorkspaceActionTypes.CREATE_WORKSPACE: {
                    const newWs = action.payload.data?.workspace ?? null;
                    const updated = newWs
                        ? [newWs, ...state.workspaces.filter(w => w.id !== newWs.id)]
                        : state.workspaces;
                    return {
                        ...state,
                        loading: false,
                        createSuccess: true,
                        workspaces: updated,
                        error: null,
                    };
                }

                case WorkspaceActionTypes.FETCH_MEMBERS:
                    return {
                        ...state,
                        loading: false,
                        members: action.payload.data?.members ?? [],
                        pendingInvitations: action.payload.data?.pending_invitations ?? [],
                    };

                case WorkspaceActionTypes.INVITE_MEMBER:
                    return {
                        ...state,
                        loading: false,
                        inviteSuccess: true,
                        memberError: null,
                    };

                case WorkspaceActionTypes.REMOVE_MEMBER:
                    return {
                        ...state,
                        loading: false,
                        removeSuccess: true,
                        members: state.members.filter(m => m.user_id !== action.payload.data?.userId),
                    };

                case WorkspaceActionTypes.UPDATE_ROLE:
                    return {
                        ...state,
                        loading: false,
                        updateRoleSuccess: true,
                    };

                case WorkspaceActionTypes.CANCEL_INVITATION:
                    return {
                        ...state,
                        loading: false,
                        cancelSuccess: true,
                        pendingInvitations: state.pendingInvitations.filter(
                            i => i.id !== action.payload.data?.invitationId
                        ),
                    };

                case WorkspaceActionTypes.ACCEPT_INVITATION:
                    return {
                        ...state,
                        loading: false,
                        acceptSuccess: true,
                        acceptedWorkspace: action.payload.data?.workspace ?? null,
                    };

                default:
                    return { ...state };
            }

        case WorkspaceActionTypes.API_RESPONSE_ERROR:
            return {
                ...state,
                loading: false,
                error: action.payload.error,
                inviteSuccess: false,
                createSuccess: false,
                switchSuccess: false,
                removeSuccess: false,
            };

        // Loading states
        case WorkspaceActionTypes.FETCH_WORKSPACES:
        case WorkspaceActionTypes.CREATE_WORKSPACE:
        case WorkspaceActionTypes.INVITE_MEMBER:
        case WorkspaceActionTypes.FETCH_MEMBERS:
        case WorkspaceActionTypes.REMOVE_MEMBER:
        case WorkspaceActionTypes.UPDATE_ROLE:
        case WorkspaceActionTypes.CANCEL_INVITATION:
        case WorkspaceActionTypes.ACCEPT_INVITATION:
            return { ...state, loading: true, error: null, memberError: null };

        case WorkspaceActionTypes.SWITCH_WORKSPACE:
            return { ...state, loading: true, switchSuccess: false };

        case WorkspaceActionTypes.RESET:
            return {
                ...INIT_STATE,
                workspaces: [],
                activeWorkspace: getStoredWorkspace(),
                error: null,
                inviteSuccess: false,
                createSuccess: false,
                switchSuccess: false,
                removeSuccess: false,
                updateRoleSuccess: false,
                cancelSuccess: false,
                acceptSuccess: false,
            };

        default:
            return { ...state };
    }
};

export default Workspace;
